import {
  EXPECTED_DAYS_IN_QUALIFIED_LEAD,
  EXPECTED_DAYS_IN_READY_FOR_PHONESCREENING,
  EXPECTED_RUNINPERIOD,
} from '../../constants'
import {
  EnrollmentAnalyticData,
  ExpectedRandomizations,
  GroupedByPassedFunnelSteps,
  PerformanceStatus,
} from '../../hooks/useStatsSingleProject'
import { dataStore } from '../../store/dataStore'
import { Analytics, ContractGoal, enrollmentFunnelInOrder, EventType } from '../../Types'

type Series = {
  date: string
  value: any
}[]

/**  dateString, y-value */
export type SeriesForPlot = [string, number][]

// jeg skal jo sorte hele listen? hvorfor kun .events?

/** Aggregation pipeline. Accepts all that have at least 1 event of an accepted type. */
export function getSeriesByEventType(data: EnrollmentAnalyticData[], acceptedType: EventType): SeriesForPlot {
  //sortEventsOldestFirst(data)
  const acceptDeducedEvents = !Boolean(dataStore.getState().statsSelectedDateRange)

  const filtered = filterByEventType(data, acceptedType, acceptDeducedEvents)
  if (acceptedType === EventType.ScheduledForScreening) console.log('scheudled for screening filtered:', filtered)
  const groupedByDate: Series = groupByDay(filtered, acceptedType)
  sortByDateInPlace(groupedByDate)
  if (acceptedType === EventType.ScheduledForScreening)
    console.log('scheudled for screening groupedByDate:', groupedByDate)
  const cumulative: Series = toCumulative(groupedByDate)
  const series: SeriesForPlot = cumulative.map((serie) => [serie.date, serie.value])

  return series
}

export function getExpectedRandoesProjectionSeries(
  randomizationSeries: SeriesForPlot,
  exptectedRandomizations?: ExpectedRandomizations
): SeriesForPlot {
  try {
    if (!exptectedRandomizations || exptectedRandomizations.passesRandomization === 0) return []

    const lastTotalRando =
      randomizationSeries[randomizationSeries.length - 1] ?? ([new Date().toISOString(), 0] as [string, number])
    const startPoint =
      randomizationSeries.length === 0 ? ([new Date().toISOString(), 0] as [string, number]) : lastTotalRando

    const endPoint = [
      exptectedRandomizations.expectedEnddate,
      Math.round(exptectedRandomizations.passesRandomization) + lastTotalRando[1],
    ] as [string, number]
    return [startPoint, endPoint]
  } catch (err) {
    console.error(err)
    return []
  }
}

export function toRelativePercentage(series: SeriesForPlot, relativeToGoalPercentage: number): SeriesForPlot {
  return series.map((dataPoint) => {
    return [dataPoint[0], Math.round(dataPoint[1] * relativeToGoalPercentage)]
  })
}

export function getExpectedScreeningsProjectionSeries(
  screeningSeries: SeriesForPlot,
  runInPeriod: undefined | number,
  exptectedRandomizations?: ExpectedRandomizations
): SeriesForPlot {
  try {
    if (!exptectedRandomizations || exptectedRandomizations.passesScreening === 0) return []

    const lastScreening =
      screeningSeries[screeningSeries.length - 1] ?? ([new Date().toISOString(), 0] as [string, number])
    const startPoint =
      screeningSeries.length === 0 ? ([new Date().toISOString(), 0] as [string, number]) : lastScreening

    const endDate =
      new Date(exptectedRandomizations.expectedEnddate).getTime() -
      1000 * 60 * 60 * 24 * (runInPeriod ?? EXPECTED_RUNINPERIOD)
    const endPoint = [
      new Date(endDate).toISOString(),
      Math.round(exptectedRandomizations.passesScreening) + lastScreening[1],
    ] as [string, number]
    return [startPoint, endPoint]
  } catch (err) {
    console.error(err)
    return []
  }
}

/** Dashed lines for the required randomizations to achieve the goal. */
export function getGoalRandoProjectionSeries(
  projectAnalytics: Analytics | undefined,
  newestGoal: ContractGoal | undefined,
  runInPeriod: number | undefined
): SeriesForPlot {
  if (!newestGoal || !projectAnalytics?.goals) return []

  const effectiveDate = projectAnalytics.goals[0].effectiveDate

  if (!effectiveDate) return []

  // 5 = phonescreen expected days, 14 = qualified lead expected days.
  const startDateOffsetDays =
    EXPECTED_DAYS_IN_READY_FOR_PHONESCREENING + EXPECTED_DAYS_IN_QUALIFIED_LEAD + (runInPeriod ?? EXPECTED_RUNINPERIOD)
  const startDate = new Date(
    new Date(effectiveDate).getTime() + 1000 * 60 * 60 * 24 * startDateOffsetDays
  ).toISOString()

  const startPoint = [startDate, 0] as [string, number]
  const formattedGoal = [newestGoal.deadline, newestGoal.totalRandomizations] as [string, number]
  return [startPoint, formattedGoal]
}

/** Dashed lines for the required screenings to achieve the goal. */
export function getGoalScreeningsProjectionSeries(
  projectAnalytics: Analytics | undefined,
  newestGoal: ContractGoal | undefined,
  runInPeriod: number | undefined,
  screeningGoal: number
): SeriesForPlot {
  if (!newestGoal || !projectAnalytics?.goals) return []

  const effectiveDate = projectAnalytics.goals[0].effectiveDate

  if (!effectiveDate) return []

  // 5 = phonescreen expected days, 14 = qualified lead expected days.
  const startDateOffsetDays = EXPECTED_DAYS_IN_READY_FOR_PHONESCREENING + EXPECTED_DAYS_IN_QUALIFIED_LEAD
  const startDate = new Date(
    new Date(effectiveDate).getTime() + 1000 * 60 * 60 * 24 * startDateOffsetDays
  ).toISOString()

  const endDate = new Date(
    new Date(newestGoal.deadline).getTime() - (runInPeriod ?? EXPECTED_RUNINPERIOD) * 1000 * 60 * 60 * 24
  ).toISOString()

  const startPoint = [startDate, 0] as [string, number]
  const formattedGoal = [endDate, screeningGoal] as [string, number]
  return [startPoint, formattedGoal]
}

/** acceptDeducedEvents example: if a responder is phonescreened we know with 100% that they have also been prescreened.
 *
 * This is helpful as syncFromCC and dbResponders arent gauranteed to have all events - so we have to deduce it.
 *
 * Also helpful when implemented new steps in the funnel.
 */
export function filterByEventType(
  data: EnrollmentAnalyticData[],
  requiredEvent: EventType,
  acceptDeducedEvents = true
) {
  const enrollmentsWithRequiredEvent = data.filter((enrollment) => {
    const foundRequiredEvent = enrollment.events.find((event) => event.eventType === requiredEvent)

    if (foundRequiredEvent || !acceptDeducedEvents) {
      return foundRequiredEvent
    }

    //
    // we now have to logically decude if the event is accepted.
    // e.g. we know that all phonescreend responders have also been prescreened.
    const funnelEventIndex = enrollmentFunnelInOrder.indexOf(requiredEvent)

    if (funnelEventIndex < 0) {
      return false
    }

    const nextEventsInFunnel = enrollmentFunnelInOrder.slice(funnelEventIndex + 1) // we already checked the current index.

    for (const nextAcceptableEvent of nextEventsInFunnel) {
      const nextEvent = enrollment.events.find((event) => event.eventType === nextAcceptableEvent)

      if (nextEvent) {
        // e.g. if a responder is phonescreened, but we are looking for prescreened, we know that they are prescreened.
        // so we add the event.
        const deducedEvent = {
          createdAt: nextEvent.createdAt,
          eventType: requiredEvent,
        }
        enrollment.events.push(deducedEvent)
        return true
      }
    }

    return false
  })

  return enrollmentsWithRequiredEvent
}

function sortByDateInPlace(data: Series) {
  data.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })
}

// Function to group data by day and sum values
function groupByDay(data: EnrollmentAnalyticData[], type: EventType): Series {
  let groupedData = {}
  data.forEach((enrollment) => {
    const event = enrollment.events.find((event) => event.eventType === type)
    if (!event) throw new Error('Tried to group before filtering type: ' + type)

    let day = event.createdAt.split('T')[0] // Extract the date part
    if (!groupedData[day]) {
      groupedData[day] = 0
    }
    groupedData[day] += 1
  })
  return Object.entries(groupedData).map(([date, value]) => ({ date, value }))
}

// Function to convert to cumulative series
function toCumulative(series) {
  let cumulativeSeries = []
  series.reduce((acc, item, index) => {
    //@ts-ignore
    cumulativeSeries[index] = { date: item.date, value: acc + item.value }
    //@ts-ignore
    return cumulativeSeries[index].value
  }, 0)
  return cumulativeSeries
}

/** prerequisite: all undo events are removed from the events array including the event that they removed. */
export function passedFunnelStep(enrollment: EnrollmentAnalyticData, eventType: EventType): boolean {
  const stepIdx = enrollmentFunnelInOrder.indexOf(eventType)

  if (stepIdx < 0) throw new Error('Event not in funnelOrder!: ' + eventType)

  const isLastStep = stepIdx === enrollmentFunnelInOrder.length - 1
  if (isLastStep) {
    return enrollment.stateType === eventType
  }

  const nextStep = enrollmentFunnelInOrder[stepIdx + 1]

  const withNextStep = filterByEventType([enrollment], nextStep)

  const passed = withNextStep.length > 0
  return passed
}

export function getCountForStates(
  grouped: GroupedByPassedFunnelSteps,
  eventTypes: EventType[],
  status: PerformanceStatus
) {
  const res: number[] = []

  for (const eventType of eventTypes) {
    res.push(grouped[eventType][status].length)
  }

  return res
}

export const repeat = (arr: any[], n: number) => [].concat(...Array(n).fill(arr))

export function toUS(n: number, digits = 0) {
  const usNumberFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits, // Maximum number of decimal places
  })

  return usNumberFormatter.format(n)
}

export function logBase(x, base) {
  return Math.log(x) / Math.log(base)
}
